import React, { useEffect, useState } from "react";
import api from "../utils/axios";
import Swal from "sweetalert2";
import {
  ShieldAlert,
  Search,
  Filter,
  CheckCircle,
  Clock,
  RefreshCw,
  Calendar,
  UserX,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  PlusCircle,
  Info,
} from "lucide-react";

interface SelfExclusionRecord {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  duration: string;
  start_date: string;
  end_date: string;
  reason: string;
  status: "active" | "expired" | string;
  created_by_type: string;
  created_by_id: number | null;
  admin_notes: string | null;
  created_at: string;
}

interface UserSearchResult {
  id: number;
  name: string;
  email: string;
  phone: string;
  self_exclusion_until: string | null;
  is_currently_excluded: boolean;
}

const DURATION_OPTIONS = [
  { value: "24h", label: "24 Hours (Cool-Off Period)", description: "Mandatory 24-hour break from gameplay and deposits." },
  { value: "7d", label: "7 Days (Short-Term Exclusion)", description: "1-week pause from account activity." },
  { value: "30d", label: "30 Days (Standard Self-Exclusion)", description: "1-month responsible gaming pause." },
  { value: "6mo", label: "6 Months (Extended Exclusion)", description: "180 days strict exclusion." },
  { value: "1yr", label: "1 Year (Long-Term Exclusion)", description: "365 days full platform exclusion." },
  { value: "5yr", label: "5 Years / Permanent (Indefinite)", description: "Maximum regulatory restriction." },
];

const SelfExclusionManagement: React.FC = () => {
  const [exclusions, setExclusions] = useState<SelfExclusionRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [userQuery, setUserQuery] = useState<string>("");
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<string>("30d");
  const [reason, setReason] = useState<string>("Responsible Gaming Request");
  const [adminNotes, setAdminNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const fetchExclusions = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/self-exclusions", {
        params: {
          page,
          limit,
          status: statusFilter,
          search: searchTerm,
        },
      });

      if (res.data?.status) {
        setExclusions(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalCount(res.data.total || 0);
      }
    } catch (err: any) {
      console.error("Error fetching exclusions:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to Fetch",
        text: err.response?.data?.message || "Could not retrieve self-exclusion records.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExclusions();
  }, [page, statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchExclusions();
  };

  const handleUserSearch = async (val: string) => {
    setUserQuery(val);
    if (val.trim().length < 2) {
      setUserSearchResults([]);
      return;
    }
    try {
      const res = await api.get("/admin/self-exclusions/search-users", {
        params: { q: val.trim() },
      });
      if (res.data?.status) {
        setUserSearchResults(res.data.data || []);
      }
    } catch (err) {
      console.error("Error searching users:", err);
    }
  };

  const handleCreateExclusion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) {
      Swal.fire({ icon: "warning", title: "Select a User", text: "Please search and select a user to exclude." });
      return;
    }

    if (!reason.trim()) {
      Swal.fire({ icon: "warning", title: "Reason Required", text: "Please enter an official compliance reason." });
      return;
    }

    const confirm = await Swal.fire({
      title: "Confirm Self-Exclusion?",
      html: `You are placing <b>${selectedUser.name}</b> (UID: ${selectedUser.id}) on <b>${selectedDuration}</b> self-exclusion.<br/><br/><span style="color:#dc2626; font-size:13px;">Under regulatory policy, exclusion cannot be revoked or bypassed prior to expiration.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Place on Exclusion",
    });

    if (!confirm.isConfirmed) return;

    try {
      setSubmitting(true);
      const res = await api.post("/admin/self-exclusions", {
        userId: selectedUser.id,
        duration: selectedDuration,
        reason: reason.trim(),
        adminNotes: adminNotes.trim(),
      });

      if (res.data?.status) {
        Swal.fire({
          icon: "success",
          title: "Exclusion Enforced",
          text: res.data.message || "User placed on self-exclusion.",
        });
        setIsModalOpen(false);
        setSelectedUser(null);
        setUserQuery("");
        setAdminNotes("");
        fetchExclusions();
      }
    } catch (err: any) {
      console.error("Error creating exclusion:", err);
      Swal.fire({
        icon: "error",
        title: "Action Failed",
        text: err.response?.data?.message || "Could not place user on self-exclusion.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatRemainingTime = (endDateStr: string) => {
    const diff = new Date(endDateStr).getTime() - Date.now();
    if (diff <= 0) return "Expired";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h remaining`;
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${mins}m remaining`;
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-8 h-8 text-red-600" />
            <h1 className="text-2xl font-bold text-gray-900">Self-Exclusion Management</h1>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Enforce responsible gaming policies, manage player exclusion periods, and view audit history.
          </p>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-all"
        >
          <PlusCircle className="w-4 h-4" />
          <span>Place User on Exclusion</span>
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-xs sm:text-sm text-amber-900 leading-relaxed">
          <strong>Mandatory Compliance Notice:</strong> Excluded users are automatically blocked from logging in, launching games, placing bets, and purchasing coin packages. In accordance with applicable sweepstakes gaming regulations, administrators are prohibited from overriding mandatory exclusion periods prematurely.
        </div>
      </div>

      {/* Filters and Search Bar */}
      <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-center">
        {/* Status Filter Tabs */}
        <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl w-full md:w-auto">
          {[
            { id: "all", label: "All History" },
            { id: "active", label: "Active Exclusions" },
            { id: "expired", label: "Expired" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setStatusFilter(tab.id);
                setPage(1);
              }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                statusFilter === tab.id
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 w-full md:w-80">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search user ID, name, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-xl outline-none focus:border-red-500"
            />
          </div>
          <button
            type="submit"
            className="px-3 py-2 bg-gray-900 text-white rounded-xl text-xs font-semibold hover:bg-gray-800"
          >
            Search
          </button>
          <button
            type="button"
            onClick={() => {
              setSearchTerm("");
              setPage(1);
              fetchExclusions();
            }}
            title="Refresh"
            className="p-2 border border-gray-300 rounded-xl hover:bg-gray-50 text-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </form>
      </div>

      {/* Exclusions Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 text-gray-700 text-xs font-bold uppercase tracking-wider border-b border-gray-200">
              <tr>
                <th className="px-4 py-3.5">ID</th>
                <th className="px-4 py-3.5">Player</th>
                <th className="px-4 py-3.5">Duration</th>
                <th className="px-4 py-3.5">Time Remaining / Status</th>
                <th className="px-4 py-3.5">Start Date</th>
                <th className="px-4 py-3.5">Expiry Date</th>
                <th className="px-4 py-3.5">Reason & Notes</th>
                <th className="px-4 py-3.5">Issued By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-red-500" />
                    <span>Loading self-exclusion records...</span>
                  </td>
                </tr>
              ) : exclusions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    <p className="font-semibold text-gray-600">No self-exclusion records found</p>
                    <p className="text-xs text-gray-400">No users match the selected filter criteria.</p>
                  </td>
                </tr>
              ) : (
                exclusions.map((item) => {
                  const isActive = item.status === "active";
                  return (
                    <tr key={item.id} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">#{item.id}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{item.user_name || "Unknown"}</div>
                        <div className="text-xs text-gray-500">{item.user_email}</div>
                        <div className="text-[11px] font-mono text-gray-400">UID: {item.user_id}</div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-bold">
                          {item.duration.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {isActive ? (
                          <div className="space-y-1">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse"></span>
                              Active Exclusion
                            </span>
                            <div className="text-xs font-medium text-red-600 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>{formatRemainingTime(item.end_date)}</span>
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                            Expired
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {new Date(item.start_date).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {new Date(item.end_date).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <div className="text-xs text-gray-800 font-medium truncate" title={item.reason}>
                          {item.reason}
                        </div>
                        {item.admin_notes && (
                          <div className="text-[11px] text-gray-400 italic truncate" title={item.admin_notes}>
                            Note: {item.admin_notes}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 capitalize">
                        <span className="font-medium text-gray-700">{item.created_by_type}</span>
                        {item.created_by_id && <span className="text-[11px] block text-gray-400">ID #{item.created_by_id}</span>}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between text-xs text-gray-600">
          <span>
            Showing page <b>{page}</b> of <b>{totalPages}</b> ({totalCount} total exclusions)
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1.5 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1.5 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal: Place User on Self-Exclusion */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <div className="flex items-center gap-2">
                <UserX className="w-5 h-5 text-red-600" />
                <h3 className="text-base font-bold text-gray-900">Place User on Self-Exclusion</h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-lg font-bold"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateExclusion} className="p-6 space-y-4">
              {/* User Search & Selection */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  1. Find & Select User <span className="text-red-500">*</span>
                </label>
                {!selectedUser ? (
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Type username, email, or UID..."
                      value={userQuery}
                      onChange={(e) => handleUserSearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl outline-none focus:border-red-500"
                    />
                    {userSearchResults.length > 0 && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto z-10">
                        {userSearchResults.map((u) => (
                          <div
                            key={u.id}
                            onClick={() => {
                              setSelectedUser(u);
                              setUserSearchResults([]);
                              setUserQuery("");
                            }}
                            className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-none flex justify-between items-center"
                          >
                            <div>
                              <div className="text-sm font-semibold text-gray-900">{u.name}</div>
                              <div className="text-xs text-gray-500">{u.email} (UID: {u.id})</div>
                            </div>
                            {u.is_currently_excluded && (
                              <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">
                                Currently Excluded
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex justify-between items-center">
                    <div>
                      <span className="text-sm font-bold text-gray-900">{selectedUser.name}</span>
                      <span className="text-xs text-gray-600 block">{selectedUser.email} (UID: #{selectedUser.id})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedUser(null)}
                      className="text-xs text-red-600 hover:text-red-800 font-bold underline"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>

              {/* Exclusion Duration */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  2. Select Exclusion Duration <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {DURATION_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex flex-col p-2.5 border rounded-xl cursor-pointer text-xs transition-all ${
                        selectedDuration === opt.value
                          ? "border-red-600 bg-red-50 text-red-900 font-bold"
                          : "border-gray-200 hover:bg-gray-50 text-gray-700"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name="duration"
                          value={opt.value}
                          checked={selectedDuration === opt.value}
                          onChange={(e) => setSelectedDuration(e.target.value)}
                          className="text-red-600"
                        />
                        <span>{opt.label}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  3. Reason for Exclusion <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Player requested exclusion / Compliance hold"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl outline-none focus:border-red-500"
                  required
                />
              </div>

              {/* Internal Admin Notes */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  4. Administrative Notes (Internal Audit)
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Record reference tickets, regulatory case numbers, or notes..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl outline-none focus:border-red-500"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-sm disabled:opacity-50 flex items-center gap-1.5"
                >
                  {submitting && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  <span>Enforce Self-Exclusion</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelfExclusionManagement;

